import Notifications from "@kyvg/vue3-notification";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { createApp } from "vue";
import VueKonva from "vue-konva";

import Masonry from "@/components/Utils/Masonry.vue";
import { useAppStore, useTeamStore, useUserStore } from "@/store/modules";

import App from "./App.vue";
import utilsMix from "./mixins/utilsMix";
import VueObserveVisibility from "./plugins/observeVisibility";
import vuetify from "./plugins/vuetify";

import "./scss/globalStyles.scss";
import "overlayscrollbars/styles/overlayscrollbars.css";
import router from "./router";
import { initializeTeamContext } from "./services/teamContext/initializeTeamContext";
import { switchCurrentTeam } from "./services/teamContext/switchCurrentTeam";
import pinia from "./store";

const clickOutsideDirective = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event, el);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

const axiosInstance = axios;
const appStore = useAppStore(pinia);
const teamStore = useTeamStore(pinia);
const userStore = useUserStore(pinia);

let initRoute = window.location.href.substring(window.location.host.length + 8);
let pushAfterInit = initRoute;

axiosInstance.interceptors.request.use(
  function (config) {
    // перехватчик запросов. Добавляет заголовок авторизации
    const whiteList = [
      "/config.json",
      "/token",
      "/registration",
      "reset",
      "password_reset",
      "confirm",
      "https://cdn-",
      "/resend",
      "/api/v1/products/?tags=welcome",
      "/api/v1/widgets/default/login",
      "-oauth2",
      "www.googleapis.com/youtube/v3/playlistItems?",
      "social/complete/",
      "/tips.json",
      "/acolorpicker.js",
    ]; // список запросов для которых не нужен токен
    let inWhiteList = false;
    whiteList.forEach((urlFromWhite) => {
      if (~config.url.indexOf(urlFromWhite)) {
        inWhiteList = true;
      }
    });
    if (inWhiteList) {
      return config;
    }
    const accessToken = localStorage.getItem("zarboAccessToken");
    if (accessToken) {
      config.headers.Authorization = "Bearer " + accessToken;
    } else {
      router.push("/login");
      throw new axios.Cancel("Cancel request. No access token " + JSON.stringify(config));
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response) {
      if (error.response.status === 401) {
        if (router.currentRoute.value.name !== "login") {
          console.error("Статус запроса 401");
          router.push("/login");
        }
      }
      if (error.response.status === 403) {
        // На 403 — меняем команду, если текущая не найдена в allTeams(наверное, кейс когда тебя выгнали)
        const result = teamStore.allTeams.some((team) => team.team.id === teamStore.selectedTeam.team.id);
        if (!result) {
          const myTeam = teamStore.myTeam;
          // if (myTeam) { // в теории не нужно, но в 61(до switchCurrentTeam) Дамир чинил запрос тегов этим
          //   localStorage.setItem("selectedTeamSlug", myTeam.team.slug);
          //   teamStore.SET_VALUE({ key: "selectedTeam", value: myTeam });
          // }
          switchCurrentTeam({
            team: myTeam,
            teamSlug: myTeam.team.slug,
            router,
            redirect: true,
            mergePermissions: utilsMix.methods.mergePermissionsMix,
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

async function getConfig() {
  const url = location.origin + "/config.json";
  let config = null;
  try {
    config = await axios.get(url);
  } catch (error) {
    console.error("Отсутствует или не заполнен config.json");
    throw new Error("Fail get config", error);
  }
  if (!config.data || Object.keys(config.data).length === 0) {
    console.error("Отсутствует или не заполнен config.json");
  }
  return config.data;
}

async function getTips() {
  const url = location.origin + "/tips.json";
  let tips = null;
  try {
    tips = await axios.get(url);
  } catch (error) {
    throw new Error("Fail get tips", error);
  }
  return tips.data;
}

async function checkAuth(config) {
  const refreshToken = localStorage.getItem("zarboRefreshToken");
  const url = config.BASE_URL + "/api/token/refresh/";
  if (initRoute.split("/")[1] === "social" || initRoute.split("/")[1] === "oauth") {
    return false;
  }

  if (!refreshToken || refreshToken === "null" || refreshToken === null) {
    pushAfterInit = "/login";
    return false;
  }
  try {
    let access = null;
    let refresh = null;
    if (utilsMix.methods.isTimeToUpdateAccessMix()) {
      // Проверяем пора ли обновить токен
      const resp = await axios.post(url, { refresh: refreshToken });
      access = resp.data.access;
      refresh = resp.data.refresh;
      localStorage.setItem("zarboAccessToken", access);
      localStorage.setItem("zarboRefreshToken", refresh);
    } else {
      access = localStorage.getItem("zarboAccessToken");
      refresh = localStorage.getItem("zarboRefreshToken");
    }

    userStore.SET_VALUE({ key: "isAuthorized", value: true });
    const decodedRefresh = jwt_decode(refresh);
    const decodedAccess = jwt_decode(access);
    localStorage.setItem("zarboExpireRefresh", decodedRefresh.exp);
    void getUserInfo(config, decodedAccess.user_id);
    return true;
  } catch (error) {
    console.info("fail update token", error);
    pushAfterInit = "/login";
    return false;
  }
}

async function getUserInfo(config, userId) {
  const url = config.BASE_URL + "/api/v1/auth/users/" + userId + "/";

  try {
    const resp = await axios.get(url);
    userStore.SET_VALUE({
      key: "user",
      value: { ...resp.data, user_id: userId },
    });
  } catch (error) {
    console.error("Fail get user info", error);
  }
}

async function addScript(config) {
  return new Promise((res, rej) => {
    const viewerUrl = new URL(config.ZARBO_VIEWER_URL, window.location.origin).href;
    const existingScript = Array.from(document.querySelectorAll("script")).find(
      (script) => script.src === viewerUrl
    );

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        res();
        return;
      }

      existingScript.addEventListener("load", () => res(), { once: true });
      existingScript.addEventListener("error", (error) => rej(error), { once: true });
      return;
    }

    var script = document.createElement("script");
    script.src = viewerUrl;
    script.async = true;
    script.onload = function () {
      script.dataset.loaded = "true";
      res();
    };
    script.onerror = function (error) {
      rej(error);
    };
    document.head.appendChild(script);
  });
}

function setAxiosSettings(config) {
  axiosInstance.defaults.baseURL = config.BASE_URL;
}

function addPreconnect(url) {
  if (!url) {
    return;
  }

  try {
    const origin = new URL(url, window.location.origin).origin;

    if (origin === window.location.origin) {
      return;
    }

    const existingPreconnect = Array.from(document.querySelectorAll('link[rel="preconnect"]')).find(
      (link) => link.href === origin + "/"
    );

    if (!existingPreconnect) {
      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = origin;
      preconnect.crossOrigin = "";
      document.head.appendChild(preconnect);
    }

    const existingDnsPrefetch = Array.from(document.querySelectorAll('link[rel="dns-prefetch"]')).find(
      (link) => link.href === origin + "/"
    );

    if (!existingDnsPrefetch) {
      const dnsPrefetch = document.createElement("link");
      dnsPrefetch.rel = "dns-prefetch";
      dnsPrefetch.href = origin;
      document.head.appendChild(dnsPrefetch);
    }
  } catch (error) {
    console.error("Fail add preconnect", error);
  }
}

function warmupConnections(config) {
  addPreconnect(config.BASE_URL);
  addPreconnect(config.ZARBO_VIEWER_URL);
  addPreconnect(config.ZARBO_VIEWER_STYLE);
}

function scheduleIdleTask(task, timeout = 2000) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(
      () => {
        task();
      },
      { timeout }
    );
    return;
  }

  setTimeout(task, 0);
}

async function initSentry(app, config) {
  const SERVICE_NAME = config.SERVICE_NAME;

  // Логика определения окружения:
  // Если "app" -> production, иначе оставляем имя сервиса (например, "release60-app")
  const sentryEnvironment = SERVICE_NAME === "app" ? "production" : SERVICE_NAME;

  try {
    const Sentry = await import("@sentry/vue");

    Sentry.init({
      app,
      dsn: config.SENTRY_DSN,
      release: SERVICE_NAME,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          blockSelector: "zarbo-viewer, model-viewer, canvas, video",
        }),
      ],

      // Уровни сэмплирования
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0, // Записывать 0% всех сессий, скушает квоту
      replaysOnErrorSampleRate: 1.0, // Записывать 100% сессий, где случилась ошибка

      // Автоматически захватывать необработанные ошибки (по умолчанию true)
      attachStacktrace: true,
      // Позволяет Sentry «заглядывать» внутрь объектов в логах
      maxValueLength: 1000,
      // Нормализация данных (глубина вложенности объектов в логах)
      normalizeDepth: 5,
      debug: sentryEnvironment !== "production",
      environment: sentryEnvironment,

      enabled: import.meta.env.PROD,
    });
  } catch (e) {
    console.error("Sentry init failed", e);
  }
}

function initSentryAfterMount(app, config) {
  if (!import.meta.env.PROD || !config?.SENTRY_DSN) {
    return;
  }

  scheduleIdleTask(() => {
    void initSentry(app, config);
  });
}

// --- lazy init OTel по первому взаимодействию ---
let otelInitPromise = null;
let otelBootstrapAttached = false;

async function loadOpenTelemetry() {
  if (otelInitPromise) {
    return otelInitPromise;
  }

  otelInitPromise = import("./otel.js")
    .then(({ initOpenTelemetry }) => initOpenTelemetry())
    .catch((e) => {
      console.error("fail get OT", e);
    });

  return otelInitPromise;
}

function initOpenTelemetryOnFirstInteraction(config) {
  if (!import.meta.env.PROD || !config?.OTLP_TRACES_URL || otelBootstrapAttached) {
    return;
  }

  otelBootstrapAttached = true;

  const passiveOptions = { passive: true };

  const cleanup = () => {
    window.removeEventListener("pointerdown", start, passiveOptions);
    window.removeEventListener("keydown", start);
    window.removeEventListener("touchstart", start, passiveOptions);
    window.removeEventListener("wheel", start, passiveOptions);
  };

  const start = () => {
    cleanup();

    const run = () => {
      void loadOpenTelemetry();
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  };

  window.addEventListener("pointerdown", start, passiveOptions);
  window.addEventListener("keydown", start);
  window.addEventListener("touchstart", start, passiveOptions);
  window.addEventListener("wheel", start, passiveOptions);
}

async function initApp() {
  const [config, tips] = await Promise.all([getConfig(), getTips()]);

  window.__ZARBO_APP_CONFIG__ = config;

  warmupConnections(config);
  initOpenTelemetryOnFirstInteraction(config);
  setAxiosSettings(config);

  if (await checkAuth(config)) {
    const savedRedirect = localStorage.getItem("zarboRedirectUrl");
    if (savedRedirect) {
      pushAfterInit = savedRedirect;
      localStorage.removeItem("zarboRedirectUrl");
    }
    await initializeTeamContext({ mergePermissions: utilsMix.methods.mergePermissionsMix, config });
  }

  const app = createApp(App);

  app.use(vuetify);
  app.use(router);
  app.use(pinia);
  app.use(Notifications);
  app.use(VueKonva);
  app.mixin(utilsMix);
  app.directive("my-custom-click-outside", clickOutsideDirective);
  app.use(VueObserveVisibility);
  app.component("masonry-layout", Masonry);

  app.config.globalProperties.$axios = axiosInstance;
  app.config.globalProperties.$config = config;
  app.config.globalProperties.$tips = tips;

  app.mount("#app");

  void addScript(config).catch((error) => {
    console.error("Fail load viewer script", error);
  });

  initSentryAfterMount(app, config);

  if (!pushAfterInit.includes("/confirm")) {
    // если pushAfterInit - основная страница, то идет ошибка NavigationDuplicated: Avoided redundant navigation to current location: "/".
    router.push(pushAfterInit).catch((error) => {
      console.error(error);
    }); // роутер работает только после инициализции приложения, потому сначала запоминаем маршрут и лишь после идём по нему
  }

  if (initRoute !== pushAfterInit) {
    appStore.SET_VALUE({ key: "initRoute", value: initRoute });
  }
}

initApp();
